<template>
  <!--
    Client contact (legacy-client -> first-class-Client migration, Phase 1).

    This card USED to collect first/last/email and fold a role_id=4
    ("Client Admin") account into ``campaign.accounts``; saving then
    POSTed /campaign/updateAdminBuyer, which ran a GLOBAL email-uniqueness
    check ("Client email already exists") that blocked legitimate
    one-client-many-stores reuse.

    A Client owns MANY Stores (the ``stores.client_id`` one-to-many FK).
    Reuse must be a PICK, not a retype. So the card is now:

      - PICK an existing Client (ClientPicker), OR
      - ADD a new Client (name + work email) -> createClient() ->
        InviteClientModal sends the first invitation.

    Both paths record a ``selectedClientId``; ``updateCampaign`` sets
    ``campaign.client_id`` to it. The parent's save orchestration calls
    the attach endpoint (POST /clients/<id>/stores) -- NOT
    /campaign/updateAdminBuyer, and NOT a role_id=4 account.
  -->
  <editor-card
    title="Client contact"
    description="The name of your contact at the client company. This person will get reports, and access to the store's client dashboard."
  >
    <div class="client-contact" data-testid="ct-client-contact">
      <!-- Current selection / attached client -->
      <div
        v-if="selectedClientName"
        class="client-contact__current"
        data-testid="ct-client-current"
      >
        <div class="client-contact__current-label">Client</div>
        <div class="client-contact__current-name" data-testid="ct-client-current-name">
          {{ selectedClientName }}
        </div>
        <!-- Invitation status of the linked client (GH #4425): without it
             nothing said whether the invite went out or was accepted. Read
             from the clients-workspace summary row, the same rollup the
             Clients list shows. -->
        <span
          v-if="inviteStatusLabel"
          class="client-contact__invite-status"
          :class="`client-contact__invite-status--${selectedInviteStatus}`"
          data-testid="ct-client-invite-status"
        >
          {{ inviteStatusLabel }}
        </span>
        <!-- Change / Add-new grouped side-by-side, styled to match the
             app's standard outlined+pill buttons. "Add new client" lives
             here (next to "Choose Existing Client") so it is always
             reachable while a client is selected -- it no longer hides
             inside the picker. It keeps the distributor-context gate
             (hidden for a non-impersonating Super Admin) because creating a
             client without a distributor scope 400s on the backend. -->
        <div class="client-contact__current-actions">
          <!-- 2026-08-12 (Jake): "Change client" -> "Choose Existing
               Client". The old pair ("Change client" / "Add new client")
               did not say what the first button actually does; the new
               label names the act. Behaviour is unchanged -- it still just
               reveals the picker. -->
          <v-btn
            variant="outlined"
            rounded="pill"
            size="small"
            data-testid="ct-client-change"
            @click="startChanging"
          >
            Choose Existing Client
          </v-btn>
          <v-btn
            v-if="hasDistributorContext"
            variant="outlined"
            rounded="pill"
            size="small"
            data-testid="ct-add-new-client"
            @click="openAddNew"
          >
            Add new client
          </v-btn>
          <!-- Remove (unlink) the client from THIS store (client-flow
               decision 1). Gated on client.update like the attach path;
               the server re-enforces it and the tenant. -->
          <v-btn
            v-if="canUnlink"
            variant="outlined"
            rounded="pill"
            size="small"
            color="error"
            data-testid="ct-client-unlink"
            @click="openUnlink"
          >
            Remove client
          </v-btn>
        </div>
      </div>

      <!-- Picker + add-new affordances (shown when no client is chosen
           yet, or when the user clicked "Change client") -->
      <div
        v-if="showPicker"
        class="client-contact__pick"
        data-testid="ct-client-pick-block"
      >
        <div class="client-contact__pick-head">
          <span class="client-contact__pick-title">Choose a client</span>
          <!-- Add-new is gated on a distributor context. Creating a
               Client is inherently distributor-scoped (clients.distributor_id
               is NOT NULL), so a Super Admin who is NOT impersonating has
               no tenant to scope a new client to -- the backend rejects
               that create with a 400. Hide the affordance rather than let
               the user walk into that error. Picking an EXISTING client is
               unaffected and stays available.

               Only rendered here when NO client is selected yet (the
               current-selection block above owns the Add-new button once a
               client is chosen, so the button never renders twice). -->
          <v-btn
            v-if="hasDistributorContext && !selectedClientId"
            variant="outlined"
            rounded="pill"
            size="small"
            data-testid="ct-add-new-client"
            @click="openAddNew"
          >
            Add new client
          </v-btn>
        </div>

        <!-- Nothing linked yet (GH #4425): the list below is the
             distributor's clients to choose FROM. Without this line, the
             first row read as a pre-selected default on a new store. -->
        <p
          v-if="!selectedClientId"
          class="client-contact__none"
          data-testid="ct-client-none"
        >
          No client is linked to this store yet. Choose one of your clients
          below, or add a new one.
        </p>

        <!-- Explain why "Add new client" is unavailable for a
             distributor-less Super Admin (SA not impersonating). -->
        <p
          v-if="!hasDistributorContext"
          class="client-contact__gated-note"
          data-testid="ct-add-new-gated-note"
        >
          Impersonate a distributor to add a new client. You can still
          select an existing client below.
        </p>

        <client-picker
          data-testid="ct-client-picker"
          :current-client-id="selectedClientId"
          :distributor-id="storeDistributorId"
          @select="onPickClient"
        />
      </div>

      <!-- Confirm removing the client from this store. The client record
           itself is untouched; only this store's link is cleared. -->
      <v-dialog
        v-model="unlinking"
        max-width="480"
        :persistent="unlinkBusy"
        role="dialog"
        aria-modal="true"
        data-testid="ct-unlink-dialog"
      >
        <v-card>
          <v-card-title class="text-h6">Remove client from this store?</v-card-title>
          <v-card-text>
            <p>
              {{ selectedClientName }} stays in your Clients list. This store
              will no longer be linked to it.
            </p>
            <p
              v-if="unlinkError"
              class="client-contact__error"
              data-testid="ct-unlink-error"
            >
              {{ unlinkError }}
            </p>
          </v-card-text>
          <v-card-actions class="client-contact__add-actions">
            <v-spacer />
            <v-btn
              variant="text"
              size="small"
              data-testid="ct-unlink-cancel"
              :disabled="unlinkBusy"
              @click="unlinking = false"
            >
              Cancel
            </v-btn>
            <v-btn
              color="error"
              size="small"
              data-testid="ct-unlink-confirm"
              :disabled="unlinkBusy"
              @click="confirmUnlink"
            >
              {{ unlinkBusy ? "Removing..." : "Remove client" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!--
        Add-new client MODAL (2026-08-12, Jake).

        This form used to render inline, directly below <client-picker>
        inside the picker block above. Stacked under the list of existing
        clients it read as PART of that list -- Jake: it "appears to
        conflate with the existing client list". Creating a client is a
        separate act from choosing one, so it is now a dialog.

        v-dialog is the store-editor's dialog idiom (ProductDeleteDialog.vue, ...). It gives focus trap, Escape-to-close
        and focus-return-to-trigger for free, so none of that is hand-rolled
        here.

        ``persistent`` is bound to ``creating`` rather than set outright:
        while idle, Escape and scrim-click SHOULD dismiss (cancelling has no
        side effects), but while a create is IN FLIGHT dismissal is blocked.
        Otherwise the dialog could close mid-request and the failure branch
        would write createError into a dialog the user can no longer see --
        while the client may in fact have been created.

        Dismissal of any kind (Cancel, Escape, scrim) flows through the
        single ``addingNew`` v-model, and the watcher on it does the state
        teardown -- so there is exactly ONE reset path and no dismissal
        route can leave partial state behind.

        The ``ct-add-new-form`` testid moved WITH the form so existing
        coverage still resolves; it now exists only while the dialog is open.
      -->
      <v-dialog
        v-model="addingNew"
        max-width="640"
        :persistent="creating"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ct-add-new-dialog-title"
        data-testid="ct-add-new-dialog"
      >
        <v-card>
          <!-- aria-labelledby points here so the dialog announces its name.
               v-card-title is only a visual heading; it is not linked to the
               dialog automatically. -->
          <v-card-title id="ct-add-new-dialog-title" class="text-h6">
            Add a new client
          </v-card-title>
          <v-divider />
          <v-card-text>
            <div class="client-contact__add-form" data-testid="ct-add-new-form">
              <v-form ref="addForm" v-model="addValid" @submit.prevent="submitNewClient">
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="newClient.name"
                      label="Client company *"
                      :rules="[(v) => !!(v && v.trim()) || 'Required']"
                      data-testid="ct-new-client-name"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="newClient.email"
                      label="Client work email *"
                      hint="The client's work email -- the sign-in invitation is sent here, not your own address."
                      persistent-hint
                      :rules="[validateEmail]"
                      data-testid="ct-new-client-email"
                    />
                  </v-col>
                </v-row>
                <!-- Optional contact name: carried on the invitation (its
                     existing invited_first_name / invited_last_name). -->
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="newClient.firstName"
                      label="Contact first name (optional)"
                      data-testid="ct-new-client-first-name"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="newClient.lastName"
                      label="Contact last name (optional)"
                      data-testid="ct-new-client-last-name"
                    />
                  </v-col>
                </v-row>
                <!-- Errors render INSIDE the dialog: a create failure keeps
                     the dialog open so the message is not stranded behind
                     it. -->
                <p
                  v-if="createError"
                  class="client-contact__error"
                  data-testid="ct-add-new-error"
                >
                  {{ createError }}
                </p>
              </v-form>
            </div>
          </v-card-text>
          <v-card-actions class="client-contact__add-actions">
            <v-spacer />
            <v-btn
              variant="text"
              size="small"
              data-testid="ct-new-client-cancel"
              :disabled="creating"
              @click="cancelAddNew"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              size="small"
              data-testid="ct-new-client-submit"
              :disabled="!canCreate || creating"
              @click="submitNewClient"
            >
              {{ creating ? "Creating..." : "Create & invite" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Invite-send confirmation (the flag was set but never rendered). -->
      <p
        v-if="inviteSentTo"
        class="client-contact__sent"
        data-testid="ct-invite-sent"
      >
        Invitation sent to {{ inviteSentTo }}.
      </p>

      <!-- Invite-send error: shown when client was created but the
           invitation POST failed. Separate from createError so it
           persists after the inline form collapses. -->
      <p
        v-if="inviteError"
        class="client-contact__error"
        data-testid="ct-invite-error"
      >
        Invitation could not be sent: {{ inviteError }}. You can resend it
        from the Clients list.
      </p>

      <div
        class="client-contact-invite-note"
        data-testid="ct-client-invite-note"
      >
        <v-icon class="client-contact-invite-note__icon">mail_outline</v-icon>
        <span>
          New clients receive an invitation email and set their own
          password on first sign-in.
        </span>
      </div>
    </div>

  </editor-card>
</template>
<script>
import { describeError } from "@/utils/safeErrorLog";
import rules from "@/mixins/rules";
import EditorCard from "./components/EditorCard.vue";
import ClientPicker from "@/pages/distributor/clients/ClientPicker.vue";
import api from "@/services/api";
import { useClientsStore } from "@/stores/clients";
import { useClientsWorkspaceStore } from "@/stores/clientsWorkspace";
import { useImpersonationStore } from "@/stores/impersonation";

// Codes from the /clients/summary rollup (_derive_invitation_status in
// api/src/endpoints/clients_workspace.py). An unknown code renders nothing
// rather than a guess.
const INVITE_STATUS_LABELS = {
  never_invited: "Not invited yet",
  invited: "Invitation sent",
  expired: "Invitation expired",
  accepted: "Invitation accepted",
  revoked: "Invitation revoked",
};

export default {
  components: {
    EditorCard,
    ClientPicker,
  },

  props: {
    campaign: Object,
    editMode: Boolean,
    // targetRole / otherRole are retained for prop-contract compatibility
    // with the parent template, but the Client contact no longer maps
    // onto an account role -- it sets stores.client_id directly.
    targetRole: Object,
    otherRole: Object,
    emailError: String,
    // The client_id persisted on the store (the parent's last-loaded or
    // last-saved link). Distinct from the card's local selection, which may
    // be unsaved.
    persistedClientId: { type: [Number, String], default: null },
  },

  inject: {
    distributorLayoutCapabilities: { default: () => [] },
  },

  mixins: [rules],

  data() {
    return {
      // The client currently chosen for this store (picked or created).
      selectedClientId: null,
      selectedClientName: "",
      // Picker is shown when no client is selected, or the user is
      // explicitly changing the selection.
      changing: false,
      // Add-new modal state. ``addingNew`` is the dialog's v-model: every
      // way of closing the dialog (Cancel, Escape, scrim click) writes it
      // to false, and the watcher below does the state teardown.
      addingNew: false,
      addValid: null,
      creating: false,
      createError: "",
      newClient: { name: "", email: "", firstName: "", lastName: "" },
      // Remove-client confirm dialog state.
      unlinking: false,
      unlinkBusy: false,
      unlinkError: "",
      // Inline invite state -- the invitation is sent directly after the
      // client is created (no modal needed; the email was already collected).
      inviteSentTo: "",
      inviteError: "",
    };
  },

  computed: {
    clientsStore() {
      return useClientsStore();
    },
    workspaceStore() {
      return useClientsWorkspaceStore();
    },
    impersonationStore() {
      return useImpersonationStore();
    },
    // Whether the current principal has a distributor to scope a new
    // Client to. A non-SA (Owner / Builder) always does. A Super Admin
    // only does while impersonating a distributor. Creating a Client
    // without one is a backend 400 (clients.distributor_id is NOT NULL),
    // so the "Add new client" affordance is gated on this. Mirrors the
    // SaImpersonationBanner predicate (isSuperAdmin && !isActive).
    isSuperAdmin() {
      return Boolean(this.$store && this.$store.getters.isSuperAdmin);
    },
    hasDistributorContext() {
      return !this.isSuperAdmin || this.impersonationStore.isActive;
    },
    // The workspace summary row for the linked client (carries the derived
    // invitation status), or null before the list has loaded.
    selectedClientRow() {
      if (this.selectedClientId == null) return null;
      return this.workspaceStore.getClientById(this.selectedClientId);
    },
    selectedInviteStatus() {
      const row = this.selectedClientRow;
      return row && row.invitation_status ? row.invitation_status : null;
    },
    inviteStatusLabel() {
      return INVITE_STATUS_LABELS[this.selectedInviteStatus] || "";
    },
    capabilities() {
      const inj = this.distributorLayoutCapabilities;
      if (Array.isArray(inj)) return inj;
      if (inj && typeof inj === "object" && Array.isArray(inj.value)) {
        return inj.value;
      }
      return [];
    },
    // Same capability the attach/detach endpoints enforce.
    canUnlink() {
      return (
        this.selectedClientId != null &&
        this.capabilities.includes("client.update")
      );
    },
    // The store's own distributor: scopes the picker server-side (a Super
    // Admin who is not impersonating sees only this distributor's clients).
    storeDistributorId() {
      return (this.campaign && this.campaign.distributor_id) || null;
    },
    showPicker() {
      return this.changing || !this.selectedClientId;
    },
    canCreate() {
      const name = (this.newClient.name || "").trim();
      const email = (this.newClient.email || "").trim();
      return !!name && this.validateEmail(email) === true;
    },
  },

  watch: {
    // Single teardown path for the add-new modal. The dialog can be closed
    // by Cancel, by Escape, or by clicking the scrim -- the latter two never
    // call cancelAddNew(), so resetting here (rather than only in the Cancel
    // handler) is what guarantees "dismiss leaves no partial state" for
    // EVERY dismissal route. Only clears on close, so opening does not race
    // with openAddNew()'s own initialization.
    addingNew(isOpen) {
      if (!isOpen) {
        this.createError = "";
        this.newClient = { name: "", email: "", firstName: "", lastName: "" };
      }
    },

    campaign: {
      immediate: true,
      handler(newVal) {
        if (!this._hasInitialized && newVal && newVal.client_id != null) {
          this._hasInitialized = true;
          this.adoptExistingClient(newVal.client_id);
        }
      },
    },
  },

  methods: {
    // The tab is valid when a client is selected. (A store can be saved
    // without a client during early setup; the section-status warn state
    // in the parent surfaces the "needs a client" nudge.)
    updateTabInvalid(value) {
      this.$emit("update-tab-invalid", "clientContact", value);
    },

    validateTab() {
      // No blocking client-side validation -- selecting a client is the
      // happy path; absence is a warn, not an error.
      return false;
    },

    async adoptExistingClient(clientId) {
      this.selectedClientId = clientId;
      this.selectedClientName = await this.resolveClientName(clientId);
      this.updateTabInvalid(false);
    },

    // The client's summary row (the one the picker lists). Tries the
    // lightweight workspace cache first, then the CRUD store; fetches the
    // workspace list if neither has it.
    async resolveClientRow(clientId) {
      if (clientId == null) return null;
      let row =
        this.workspaceStore.getClientById(clientId) ||
        this.clientsStore.getClientById(clientId);
      if (!row) {
        await this.workspaceStore.fetchClients(this.storeDistributorId);
        row = this.workspaceStore.getClientById(clientId);
      }
      return row || null;
    },

    // The label the card shows for the client: the server's `display_name`
    // (company, else contact person), as the picker prints it.
    async resolveClientName(clientId) {
      if (clientId == null) return "";
      const row = await this.resolveClientRow(clientId);
      const label = row && (row.display_name || row.name);
      return label || `Client #${clientId}`;
    },

    // Tell the editor which company the attached client supplies, so the
    // Client Company Name field's default follows the picked client before
    // anything is saved (card #499). The company is the client's `name`;
    // "" when there is no client (or it has no company).
    announceClientCompany(company) {
      this.$emit(
        "client-company-changed",
        typeof company === "string" ? company.trim() : "",
      );
    },

    openUnlink() {
      if (!this.canUnlink) return;
      this.unlinkError = "";
      this.unlinking = true;
    },

    // Detach the PERSISTED link through the guarded endpoint
    // (DELETE /clients/<id>/stores/<sid>), then clear the card. A selection
    // that was never saved has nothing server-side to remove.
    async confirmUnlink() {
      if (this.unlinkBusy) return;
      this.unlinkBusy = true;
      this.unlinkError = "";
      try {
        const storeId =
          this.campaign &&
          ((this.campaign._jv && this.campaign._jv.id) || this.campaign.id);
        if (storeId && this.persistedClientId != null) {
          await this.clientsStore.removeStoreAssociation(
            this.persistedClientId,
            storeId,
          );
        }
        this.selectedClientId = null;
        this.selectedClientName = "";
        this.announceClientCompany("");
        this.changing = false;
        this.inviteSentTo = "";
        this.inviteError = "";
        this.unlinking = false;
        this.updateTabInvalid(false);
        this.$emit("client-unlinked");
        this.$emit("campaign-changed");
      } catch (err) {
        this.unlinkError =
          this.extractCreateError(err) ||
          "Could not remove the client. Try again.";
      } finally {
        this.unlinkBusy = false;
      }
    },

    startChanging() {
      this.changing = true;
    },

    async onPickClient(clientId) {
      this.selectedClientId = clientId;
      this.selectedClientName = await this.resolveClientName(clientId);
      const pickedRow = await this.resolveClientRow(clientId);
      this.announceClientCompany(pickedRow && pickedRow.name);
      this.changing = false;
      this.addingNew = false;
      this.createError = "";
      this.inviteSentTo = "";
      this.inviteError = "";
      this.updateTabInvalid(false);
      this.$emit("campaign-changed");
    },

    openAddNew() {
      // Defensive: the affordance is hidden without a distributor
      // context, but never open the form if somehow invoked anyway --
      // the create would 400.
      if (!this.hasDistributorContext) return;
      // The form lives in a dialog now (2026-08-12), NOT inside the picker
      // block, so opening it no longer has to force the picker open. The
      // previous implementation set ``changing = true`` purely to make the
      // inline form render; doing that here would now be a visible side
      // effect -- expanding the existing-client list behind the modal for
      // no reason -- so it is deliberately gone.
      this.createError = "";
      this.newClient = { name: "", email: "", firstName: "", lastName: "" };
      this.addingNew = true;
    },

    cancelAddNew() {
      // Closing is enough: the ``addingNew`` watcher clears the form and any
      // error, so Cancel, Escape and scrim-click all reset identically.
      this.addingNew = false;
    },

    async submitNewClient() {
      if (!this.canCreate || this.creating) return;
      this.creating = true;
      this.createError = "";
      this.inviteSentTo = "";
      this.inviteError = "";
      try {
        // Read the typed values BEFORE closing the dialog: closing fires the
        // ``addingNew`` watcher, which clears ``newClient``.
        const email = this.newClient.email.trim().toLowerCase();
        const typedName = this.newClient.name.trim();
        const firstName = (this.newClient.firstName || "").trim();
        const lastName = (this.newClient.lastName || "").trim();
        const created = await this.clientsStore.createClient({
          name: typedName,
          email,
          status: "active",
        });
        if (!created || created.id == null) {
          throw new Error("Client creation returned no id");
        }
        this.selectedClientId = created.id;
        this.selectedClientName = created.name || typedName;
        this.announceClientCompany(created.name || typedName);
        // Success closes the dialog. The watcher clears the form + any
        // stale createError, so the create error slot cannot leak into the
        // next open.
        this.addingNew = false;
        this.changing = false;

        // Send the invitation inline using the email already collected --
        // no modal needed. The invite endpoint only requires the email;
        // first/last name are optional and sent only when typed.
        try {
          const invitation = { email };
          if (firstName) invitation.first_name = firstName;
          if (lastName) invitation.last_name = lastName;
          await api.post(`/clients/${created.id}/invitations`, invitation);
          this.inviteSentTo = email;
        } catch (inviteErr) {
          // The client was created successfully; only the invite failed.
          // Surface the invite error so the user can retry (e.g. via the
          // Clients list's "Resend invitation" action), but don't roll
          // back the client creation.
          //
          // This goes to ``inviteError`` ONLY -- it is rendered by the
          // card-level ct-invite-error slot, which outlives the dialog.
          // Writing it to createError (as this did while the form was
          // inline) would now strand the message inside a dialog that has
          // already closed, and the watcher would clear it anyway.
          this.inviteError = this.extractCreateError(inviteErr);
        }

        // Refresh the summary so the new client (and its invitation status)
        // is in the list the picker and the status badge read. Best-effort:
        // the client + invite already persisted, so a failed refresh only
        // delays the badge.
        try {
          await this.workspaceStore.fetchClients(this.storeDistributorId);
        } catch (refreshErr) {
          console.warn("Client list refresh failed after create", describeError(refreshErr));
        }

        this.updateTabInvalid(false);
        this.$emit("campaign-changed");
      } catch (err) {
        this.createError = this.extractCreateError(err);
      } finally {
        this.creating = false;
      }
    },

    // Pull a human-readable message out of a failed createClient call.
    // The backend returns errors in two shapes:
    //   * custom Blueprint / abort(): { message: "..." }
    //   * SAFRS surface (POST /clients/): { errors: [{ detail: "..." }] }
    // The distributor-context 400 comes through the SAFRS surface, so the
    // ``errors[].detail`` path is the one that surfaces this fix's message.
    extractCreateError(err) {
      const data = err && err.response && err.response.data;
      if (data) {
        if (typeof data.message === "string" && data.message) {
          return data.message;
        }
        if (Array.isArray(data.errors) && data.errors.length) {
          const first = data.errors[0] || {};
          const detail = first.detail || first.title;
          if (typeof detail === "string" && detail) return detail;
        }
      }
      if (err && err.message) return err.message;
      return "Could not create the client. Try again.";
    },

    // Called by the parent save loop. Records the selected client on the
    // campaign so the parent can attach it via POST /clients/<id>/stores.
    // Does NOT push a role_id=4 account (the legacy contact write path).
    async updateCampaign(campaign) {
      campaign.error = null;
      if (this.selectedClientId != null) {
        campaign.client_id = this.selectedClientId;
      }
      return campaign;
    },
  },
};
</script>

<style scoped>
.client-contact {
  display: flex;
  flex-direction: column;
  gap: var(--tf-sp-3, 12px);
}
.client-contact__current {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--tf-sp-3, 12px);
}
.client-contact__current-label {
  font-size: 12px;
  color: var(--tf-fg-3);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.client-contact__current-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--tf-fg-1);
}
.client-contact__current-actions {
  display: flex;
  align-items: center;
  gap: var(--tf-sp-2, 8px);
}
.client-contact__pick-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--tf-sp-2, 8px);
}
.client-contact__pick-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--tf-fg-2);
}
.client-contact__add-form {
  margin-top: var(--tf-sp-3, 12px);
  padding-top: var(--tf-sp-3, 12px);
  border-top: 1px solid var(--tf-border, #d0d4dc);
}
.client-contact__add-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--tf-sp-2, 8px);
}
.client-contact__error {
  color: #c0392b;
  font-size: 13px;
  margin: 0 0 8px;
}
.client-contact__invite-status {
  white-space: nowrap;
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 999px;
  border: 1px solid var(--tf-border, #d0d4dc);
  color: var(--tf-fg-2);
}
.client-contact__invite-status--accepted {
  color: var(--tf-success-dark, #2e7d32);
  background: var(--tf-success-soft, #e6f7ed);
  border-color: transparent;
}
.client-contact__invite-status--invited {
  color: var(--tf-warning-dark, #8a6d3b);
  background: var(--tf-warning-soft, #fff8e1);
  border-color: transparent;
}
.client-contact__invite-status--expired,
.client-contact__invite-status--revoked {
  color: var(--tf-error-dark, #c62828);
  background: var(--tf-error-soft, #fdecec);
  border-color: transparent;
}
.client-contact__none {
  font-size: 13px;
  color: var(--tf-fg-2);
  margin: 0 0 var(--tf-sp-2, 8px);
}
.client-contact__sent {
  font-size: 13px;
  color: var(--tf-fg-2);
  margin: 0 0 8px;
}
.client-contact__gated-note {
  font-size: 13px;
  color: var(--tf-fg-3);
  margin: 0 0 var(--tf-sp-2, 8px);
}
.client-contact-invite-note {
  display: flex;
  align-items: center;
  gap: var(--tf-sp-2, 8px);
  margin-top: var(--tf-sp-3, 12px);
  font-size: 13px;
  color: var(--tf-fg-3);
}
.client-contact-invite-note__icon {
  font-size: 14px;
  color: var(--tf-fg-4);
}
</style>
